import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Landmark, PiggyBank, FileText, Scale, Building2, Droplets, HandCoins, Layers, BookOpenCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PageHeader, SectionHeader } from '../components/Shared';
import { FactBadge, FactCard, PositionBox, QuestionBox, FaqList } from '../components/FactCheck';
import { SourceLinkCard } from '../components/DataSources';
import { NextStepBand } from '../components/NextStepBand';

const ACCESSED = 'August 25, 2026';

const FUNDING_SOURCES = [
  { icon: Landmark, title: 'Property taxes (the levy)', body: 'The tax bill on homes, farms, and businesses. For 2026, the general levy is about $136.5 million.' },
  { icon: Droplets, title: 'User fees and rates', body: 'Water and wastewater bills, transit fares, permits, and recreation fees. Water and wastewater largely pay for themselves through rates, not property taxes.' },
  { icon: HandCoins, title: 'Provincial and federal funding', body: 'Grants and transfers for specific programs, infrastructure, and services.' },
  { icon: Building2, title: 'Development charges', body: 'Fees on new construction that help pay for the growth-related infrastructure it requires.' },
  { icon: Layers, title: 'Reserves and debt', body: 'Savings set aside for future needs, and borrowing for major long-life projects, both governed by policy and provincial rules.' },
];

const FAQS = [
  { q: 'Is the whole $472 million paid by property taxes?', a: 'No, and this matters. The $472 million budget is funded by a mix of property taxes, user fees and rates (like water bills), provincial and federal funding, development charges, reserves, and borrowing. The general property tax levy for 2026 is about $136.5 million of it.' },
  { q: 'So can you just subtract the levy from the budget to find a shortfall?', a: 'No. Subtracting the property tax levy from the total budget and calling the rest a shortfall is an oversimplified calculation, because the rest is funded by other legitimate revenue sources. Honest accountability starts with describing the funding correctly.' },
  { q: 'Where can I see deficits, surpluses, reserves, and debt?', a: 'In the City\'s audited financial statements and budget documents, linked below. Ontario municipalities cannot legally run operating deficits the way senior governments do; year-end differences flow to or from reserves.' },
  { q: 'Then what is the problem Steve is raising?', a: 'The pace of cost growth and whether taxpayers get full value. Facts about how the budget is funded do not answer whether every dollar is spent well. That is what the Savings Tracker, the Sunshine List dashboard, and the forensic audit call are for.' },
];

export default function CityFinances() {
  return (
    <div>
      <PageHeader
        pageKey="city-finances"
        kicker="YOUR MONEY"
        title="City finances, explained honestly"
        subtitle="The budget, the levy, reserves, and debt in plain language, with official sources, and without oversimplified math."
      />

      {/* The 2026 budget: verified */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="finances-budget-section">
        <SectionHeader kicker="THE CURRENT BUDGET" title="The 2026 budget at a glance" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <FactCard kind="official_source" title="$472 million total budget"
            cite={{ source: 'City of Kawartha Lakes, 2026 Budget adoption notice', year: '2025', accessed: ACCESSED, url: 'https://www.kawarthalakes.ca/news-and-notices/posts/kawartha-lakes-council-adopts-the-2026-budget-centered-on-strategic-priorities/' }}>
            Adopted November 25, 2025. Covers operating, capital, special projects, and water and wastewater.
          </FactCard>
          <FactCard kind="official_source" title="About $136.5 million general tax levy"
            cite={{ source: 'City of Kawartha Lakes Council, 2026 taxation estimates', year: '2026', accessed: ACCESSED, url: 'https://www.kawarthalakes.ca/budget' }}>
            The general levy adopted for 2026 is $136,538,998. The 5.9% levy increase is 2.9% operations, 2.0% dedicated capital, and 1.0% special area rates for policing and Lindsay Transit.
          </FactCard>
          <FactCard kind="official_source" title="$126 million capital program"
            cite={{ source: 'City of Kawartha Lakes, 2026 tax insert', year: '2026', accessed: ACCESSED, url: 'https://www.kawarthalakes.ca/media/osvpigpb/kl-tax-insert-jan-2026_accessible.pdf' }}>
            2026 capital spending includes about $58.9 million for roads and bridges, $48.2 million for facilities and siteworks, and $19.7 million for fleet and equipment.
          </FactCard>
        </div>
      </section>

      {/* How funding actually works */}
      <section className="bg-white border-y border-slate-200 py-10 md:py-14" data-testid="finances-funding-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader kicker="NO OVERSIMPLIFIED MATH" title="How the budget is actually funded" dek="Property taxes are one source among several. Describing this correctly is part of honest accountability." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FUNDING_SOURCES.map(({ icon: Icon, title, body }) => (
              <Card key={title} className="card-lift" data-testid="finances-funding-card">
                <CardContent className="p-5">
                  <div className="h-10 w-10 rounded-xl bg-[#1a2744] flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5 text-[#C9A84C]" />
                  </div>
                  <p className="font-serif font-bold text-[#1a2744] leading-snug">{title}</p>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{body}</p>
                </CardContent>
              </Card>
            ))}
            <Card className="border-amber-300 bg-amber-50/60" data-testid="finances-warning-card">
              <CardContent className="p-5">
                <div className="h-10 w-10 rounded-xl bg-amber-100 border border-amber-300 flex items-center justify-center mb-3">
                  <Scale className="h-5 w-5 text-amber-700" />
                </div>
                <p className="font-serif font-bold text-amber-950 leading-snug">A common mistake to avoid</p>
                <p className="text-sm text-amber-900 mt-1.5 leading-relaxed">Do not subtract the property tax levy from the total budget and call everything left a shortfall. The difference is funded by the other revenue sources above. Real accountability questions are about value and cost growth, not fabricated gaps.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Reserves, debt, long-term plan: official records */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="finances-records-section">
        <SectionHeader kicker="THE DEEP RECORDS" title="Deficits, surpluses, reserves, and debt" dek="These live in the City's audited statements and financial plans. Go straight to the official records." />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SourceLinkCard icon={BookOpenCheck} title="Audited financial statements" body="The independently audited year-end statements: reserves, debt, surpluses, and spending by category." url="https://www.kawarthalakes.ca/en/municipal-services/budget-and-financial-statements.aspx" label="View official statements" />
          <SourceLinkCard icon={FileText} title="Budget documents and long-term plan" body="The City's budget portal: current budget books, the long-term financial plan, and updates." url="https://www.kawarthalakes.ca/budget" label="View the budget portal" />
          <SourceLinkCard icon={Landmark} title="Council reports and minutes" body="The official record of financial decisions as Council makes them." url="https://www.kawarthalakes.ca/en/city-hall/council-meetings.aspx" label="View Council records" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <QuestionBox testId="finances-question-1">Is spending growing faster than population, inflation, and the services residents actually receive?</QuestionBox>
          <QuestionBox testId="finances-question-2">Are reserves and debt being used for long-term value, or to smooth over structural cost growth?</QuestionBox>
        </div>
        <div className="mt-6">
          <PositionBox title="Cut the cost of government before asking taxpayers for more." testId="finances-position-band">
            <p>The numbers above are the honest starting point. The work is making sure every one of those dollars produces value, and showing that work in public.</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <Button asChild className="h-11 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="finances-lower-taxes-button"><Link to="/lower-taxes">The Lower Taxes Plan <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
              <Button asChild variant="outline" className="h-11 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press" data-testid="finances-savings-button"><Link to="/savings-tracker"><PiggyBank className="h-4 w-4 mr-1.5" /> Savings Tracker</Link></Button>
            </div>
          </PositionBox>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-slate-200 py-10 md:py-14" data-testid="finances-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader kicker="PLAIN ANSWERS" title="Common questions" />
          <FaqList items={FAQS} testId="finances-faq" />
        </div>
      </section>

      {/* One clear next step */}
      <div className="pt-10">
        <NextStepBand
          title="Now watch the money being tracked, item by item."
          buttonLabel="Open the Savings Tracker"
          to="/savings-tracker"
          secondaryLabel="Or see the Sunshine List dashboard"
          secondaryTo="/sunshine-list"
          testId="finances-next-step"
        />
      </div>
    </div>
  );
}
